import React, { useState, useEffect, useRef } from "react";
import { Save, X, Bold, Italic, List, ListOrdered, Link, Code, Image, Eye, Users, Lock } from "lucide-react";
import Modal from "../ui/Modal";
import { ApiService } from "../../services/api";
import { useModal } from "../../hooks/useModal";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";

interface EditSummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  meetingId: string;
  meetingTitle: string;
  initialSummary?: {
    title?: string;
    overview?: string;
    content?: string;
    details?: any;
    nextSteps?: any;
    show?: boolean;
  };
  onSummaryUpdated: () => void;
}

export default function EditSummaryModal({
  isOpen,
  onClose,
  meetingId,
  meetingTitle,
  initialSummary,
  onSummaryUpdated,
}: EditSummaryModalProps) {
  const { showAlert } = useModal();
  const [loading, setLoading] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [formData, setFormData] = useState({
    title: "",
    content: "",
    show: false,
  });

  useEffect(() => {
    if (isOpen && initialSummary) {
      setFormData({
        title: initialSummary.title || "",
        content: initialSummary.content || "",
        show: initialSummary.show || false,
      });
    }
  }, [isOpen, initialSummary]);

  const insertMarkdown = (prefix: string, suffix: string = "", placeholder: string = "") => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selectedText = formData.content.substring(start, end);
    const textToInsert = selectedText || placeholder;
    const newText = formData.content.substring(0, start) + prefix + textToInsert + suffix + formData.content.substring(end);

    setFormData((prev) => ({ ...prev, content: newText }));

    setTimeout(() => {
      textarea.focus();
      const newCursorPos = start + prefix.length + textToInsert.length;
      textarea.setSelectionRange(newCursorPos, newCursorPos);
    }, 0);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      const title = formData.title.trim();
      const content = formData.content.trim();

      if (!title) {
        await showAlert({
          title: "Uyarı",
          message: "Lütfen başlık alanını doldurun.",
          type: "warning",
        });
        setLoading(false);
        return;
      }

      const summaryData: any = {
        title: title,
        content: content,
        show: formData.show,
      };

      await ApiService.updateMeetingSummary(meetingId, summaryData);

      await showAlert({
        title: "Başarılı",
        message: "Toplantı özeti başarıyla güncellendi.",
        type: "success",
      });

      onSummaryUpdated();
      onClose();
    } catch (error: any) {
      console.error("Özet güncelleme hatası:", error);
      await showAlert({
        title: "Hata",
        message: error.message || "Özet güncellenirken bir hata oluştu.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={`${meetingTitle} - Özet Düzenle`} size="lg">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
            Başlık
          </label>
          <input
            type="text"
            id="title"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="Özet başlığı..."
          />
        </div>

        <div className="space-y-2">
          <div className="flex items-center justify-between p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setFormData({ ...formData, show: !formData.show })}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  formData.show ? "bg-green-600" : "bg-gray-300"
                }`}
                aria-pressed={formData.show}
                aria-labelledby="show">
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    formData.show ? "translate-x-6" : "translate-x-1"
                  }`}
                />
              </button>
              <label htmlFor="show" className="text-sm font-medium text-gray-700 cursor-pointer">
                Özeti Katılımcılara Göster
              </label>
            </div>
            <div className="text-xs font-medium flex items-center gap-1">
              {formData.show ? (
                <>
                  <Users className="w-3 h-3 text-green-600" />
                  <span className="text-green-600">Tüm Katılımcılar Görebilir</span>
                </>
              ) : (
                <>
                  <Lock className="w-3 h-3 text-orange-600" />
                  <span className="text-orange-600">Sadece Toplantı Sahibi Görebilir</span>
                </>
              )}
            </div>
          </div>
          <p className="text-xs text-gray-500 px-1">
            <strong>Bilgi:</strong> Bu seçenek işaretli değilse, özet sadece toplantı sahibi tarafından görülebilir. Katılımcılar özeti
            göremez.
          </p>
        </div>

        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-medium text-gray-700">Detaylı İçerik</label>
            <button
              type="button"
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-gray-600 hover:text-gray-900 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors">
              <Eye size={14} />
              {showPreview ? "Düzenle" : "Önizleme"}
            </button>
          </div>

          {!showPreview && (
            <>
              <div className="flex flex-wrap gap-1 mb-2 p-2 bg-gray-50 rounded-lg border border-gray-200">
                <button
                  type="button"
                  onClick={() => insertMarkdown("**", "**", "kalın yazı")}
                  className="p-1.5 hover:bg-white rounded text-gray-600 hover:text-gray-900 transition-colors border border-transparent hover:border-gray-300"
                  title="Kalın (Bold)">
                  <Bold size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => insertMarkdown("*", "*", "italik yazı")}
                  className="p-1.5 hover:bg-white rounded text-gray-600 hover:text-gray-900 transition-colors border border-transparent hover:border-gray-300"
                  title="İtalik (Italic)">
                  <Italic size={16} />
                </button>
                <div className="w-px bg-gray-300 mx-1"></div>
                <button
                  type="button"
                  onClick={() => insertMarkdown("\n- ", "", "liste öğesi")}
                  className="p-1.5 hover:bg-white rounded text-gray-600 hover:text-gray-900 transition-colors border border-transparent hover:border-gray-300"
                  title="Madde İşaretli Liste">
                  <List size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => insertMarkdown("\n1. ", "", "liste öğesi")}
                  className="p-1.5 hover:bg-white rounded text-gray-600 hover:text-gray-900 transition-colors border border-transparent hover:border-gray-300"
                  title="Numaralı Liste">
                  <ListOrdered size={16} />
                </button>
                <div className="w-px bg-gray-300 mx-1"></div>
                <button
                  type="button"
                  onClick={() => insertMarkdown("[", "](url)", "link metni")}
                  className="p-1.5 hover:bg-white rounded text-gray-600 hover:text-gray-900 transition-colors border border-transparent hover:border-gray-300"
                  title="Link Ekle">
                  <Link size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => insertMarkdown("`", "`", "kod")}
                  className="p-1.5 hover:bg-white rounded text-gray-600 hover:text-gray-900 transition-colors border border-transparent hover:border-gray-300"
                  title="Kod (Inline)">
                  <Code size={16} />
                </button>
                <button
                  type="button"
                  onClick={() => insertMarkdown("![", "](url)", "resim açıklaması")}
                  className="p-1.5 hover:bg-white rounded text-gray-600 hover:text-gray-900 transition-colors border border-transparent hover:border-gray-300"
                  title="Resim Ekle">
                  <Image size={16} />
                </button>
              </div>

              <textarea
                ref={textareaRef}
                id="content"
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                rows={10}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                placeholder="Toplantının detaylı içeriğini buraya yazın... (Markdown destekli)"
              />
            </>
          )}

          {showPreview && (
            <div className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 min-h-[250px] prose prose-sm max-w-none overflow-hidden">
              {formData.content ? (
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeHighlight]}
                  components={{
                    a: ({ children, href }) => (
                      <a
                        href={href}
                        className="text-blue-600 hover:underline font-medium"
                        style={{
                          wordBreak: "break-word",
                          overflowWrap: "break-word",
                        }}
                        target="_blank"
                        rel="noopener noreferrer">
                        {children}
                      </a>
                    ),
                    p: ({ children }) => <p className="mb-2">{children}</p>,
                    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
                    em: ({ children }) => <em className="italic">{children}</em>,
                    h1: ({ children }) => <h1 className="text-2xl font-bold mb-4">{children}</h1>,
                    h2: ({ children }) => <h2 className="text-xl font-semibold mb-3">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-lg font-semibold mb-2">{children}</h3>,
                    ul: ({ children }) => <ul className="list-disc ml-6 mb-4">{children}</ul>,
                    ol: ({ children }) => <ol className="list-decimal ml-6 mb-4">{children}</ol>,
                    li: ({ children }) => <li className="mb-1">{children}</li>,
                    code: ({ children, className }) =>
                      className ? (
                        <code className="bg-gray-800 text-gray-100 px-2 py-1 rounded text-sm block">{children}</code>
                      ) : (
                        <code className="bg-gray-200 text-gray-800 px-1 py-0.5 rounded text-sm">{children}</code>
                      ),
                    pre: ({ children }) => (
                      <pre className="bg-gray-800 text-gray-100 p-3 rounded-lg overflow-x-auto text-sm mb-4">{children}</pre>
                    ),
                    blockquote: ({ children }) => (
                      <blockquote className="border-l-4 border-gray-300 pl-4 italic text-gray-600 my-4">{children}</blockquote>
                    ),
                  }}>
                  {formData.content}
                </ReactMarkdown>
              ) : (
                <p className="text-gray-400 text-sm italic">Önizlemek için içerik yazın...</p>
              )}
            </div>
          )}

          <p className="mt-1 text-xs text-gray-500">
            <strong>Markdown kullanabilirsiniz:</strong> **kalın**, *italik*, [link](url), `kod`, listeler vb.
          </p>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
            <X className="w-4 h-4" />
            İptal
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white hover:bg-blue-700 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2">
            <Save className="w-4 h-4" />
            {loading ? "Kaydediliyor..." : "Kaydet"}
          </button>
        </div>
      </form>
    </Modal>
  );
}
